/**
 * Shared config API client — JWT authenticated.
 * All requests use authFetch which injects the JWT and handles refresh.
 */

import { authFetch } from "./authClient";
import { ensureFreshStoredAuth } from "./auth";
import { expireAuthSession } from "./authStore";

// ── Types ────────────────────────────────────────────────────────────

export interface SharedConfigDocument {
  configId: string;
  payload: string;
  version: number;
  updatedAt: string;
  updatedBy: { userId: string; displayName: string; avatarUrl: string | null } | null;
}

export interface SharedConfigMember {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  email: string;
  role: "owner" | "editor";
  joinedAt: string;
  lastSeenAt: string;
}

export interface SharedConfigStreamEvent {
  event: "ready" | "changed" | "deleted";
  currentVersion: number;
  updatedAt: string;
  payload?: string;
}

// ── CRUD ─────────────────────────────────────────────────────────────

export async function createSharedConfig(payload: string): Promise<SharedConfigDocument> {
  const res = await authFetch("/shared-configs", {
    method: "POST",
    body: JSON.stringify({ payload }),
  });
  if (!res.ok) throw new Error("Failed to create shared config");
  return res.json() as Promise<SharedConfigDocument>;
}

export async function getSharedConfig(configId: string): Promise<SharedConfigDocument> {
  const res = await authFetch(`/shared-configs/${configId}`);
  if (!res.ok) throw new Error("Failed to read shared config");
  return res.json() as Promise<SharedConfigDocument>;
}

export async function updateSharedConfig(
  configId: string,
  payload: string,
  baseVersion: number,
): Promise<SharedConfigDocument> {
  const res = await authFetch(`/shared-configs/${configId}`, {
    method: "PUT",
    body: JSON.stringify({ payload, baseVersion }),
  });
  if (!res.ok) {
    if (res.status === 409) throw new Error("Conflict — shared config was updated by someone else");
    throw new Error("Failed to update shared config");
  }
  return res.json() as Promise<SharedConfigDocument>;
}

export async function deleteSharedConfig(configId: string): Promise<void> {
  const res = await authFetch(`/shared-configs/${configId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete shared config");
}

// ── Members ──────────────────────────────────────────────────────────

export async function listSharedConfigMembers(configId: string): Promise<{ members: SharedConfigMember[] }> {
  const res = await authFetch(`/shared-configs/${configId}/members`);
  if (!res.ok) throw new Error("Failed to list members");
  return res.json() as Promise<{ members: SharedConfigMember[] }>;
}

export async function kickSharedConfigMember(configId: string, userId: string): Promise<void> {
  const res = await authFetch(`/shared-configs/${configId}/members/${userId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to kick member");
}

export async function changeSharedConfigMemberRole(configId: string, userId: string, role: "owner" | "editor"): Promise<void> {
  const res = await authFetch(`/shared-configs/${configId}/members/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error("Failed to change role");
}

export async function transferSharedConfigOwnership(configId: string, newOwnerUserId: string): Promise<void> {
  const res = await authFetch(`/shared-configs/${configId}/transfer`, {
    method: "POST",
    body: JSON.stringify({ newOwnerUserId }),
  });
  if (!res.ok) throw new Error("Failed to transfer ownership");
}

// ── Invites ──────────────────────────────────────────────────────────

export interface SharedConfigInvite {
  id: string;
  configId: string;
  email: string;
  role: "owner" | "editor";
  createdAt: string;
  expiresAt: string;
  inviteUrl?: string;
}

export interface SharedConfigPendingInvite {
  id: string;
  configId: string;
  email: string;
  role: "owner" | "editor";
  createdAt: string;
  expiresAt: string;
  inviteUrl: string;
}

export interface PendingInvite {
  id: string;
  config_id: string;
  role: string;
  created_at: string;
  expires_at: string;
  invited_by_name: string;
}

export async function inviteToSharedConfig(configId: string, email: string, role: "owner" | "editor" = "editor"): Promise<SharedConfigInvite> {
  const res = await authFetch(`/shared-configs/${configId}/invite`, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to send invite");
  }
  return res.json() as Promise<SharedConfigInvite>;
}

export async function acceptSharedConfigInvite(configId: string): Promise<{ configId: string; role: string; joinedAt: string }> {
  const res = await authFetch(`/shared-configs/${configId}/join`, { method: "POST" });
  if (!res.ok) throw new Error("Failed to accept invite");
  return res.json() as Promise<{ configId: string; role: string; joinedAt: string }>;
}

export async function listSharedConfigPendingInvites(configId: string): Promise<{ invites: SharedConfigPendingInvite[] }> {
  const res = await authFetch(`/shared-configs/${configId}/invites`);
  if (!res.ok) throw new Error("Failed to list pending invites");
  return res.json() as Promise<{ invites: SharedConfigPendingInvite[] }>;
}

export async function resendSharedConfigInvite(configId: string, inviteId: string): Promise<SharedConfigInvite> {
  const res = await authFetch(`/shared-configs/${configId}/invites/${inviteId}/resend`, { method: "POST" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to resend invite");
  }
  return res.json() as Promise<SharedConfigInvite>;
}

export async function revokeSharedConfigInvite(configId: string, inviteId: string): Promise<void> {
  const res = await authFetch(`/shared-configs/${configId}/invites/${inviteId}`, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to revoke invite");
  }
}

export async function listPendingInvites(): Promise<{ invites: PendingInvite[] }> {
  const res = await authFetch("/user/invites");
  if (!res.ok) throw new Error("Failed to list invites");
  return res.json() as Promise<{ invites: PendingInvite[] }>;
}

// ── SSE stream ───────────────────────────────────────────────────────

const API_BASE = (
  (import.meta.env.VITE_SHARED_CONFIG_API_BASE as string | undefined)
  ?? "https://spuerhund-api.cloudeflare-8a5.workers.dev/api"
).replace(/\/$/, "");

export function openSharedConfigStream(
  configId: string,
  sinceVersion: number,
  onEvent: (event: SharedConfigStreamEvent) => void,
): { close: () => void; ready: Promise<void> } {
  const controller = new AbortController();
  let closed = false;

  const ready = (async () => {
    const auth = await ensureFreshStoredAuth();

    const response = await fetch(
      `${API_BASE}/shared-configs/${configId}/stream?sinceVersion=${sinceVersion}`,
      {
        headers: { Authorization: `Bearer ${auth.accessToken}` },
        signal: controller.signal,
      },
    );

    if (response.status === 401) {
      expireAuthSession();
      throw new Error("Session expired");
    }
    if (!response.ok) throw new Error(`Shared config stream failed: HTTP ${response.status}`);
    if (!response.body) throw new Error("Shared config stream not available");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (!closed) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      for (;;) {
        const boundary = buffer.indexOf("\n\n");
        if (boundary === -1) break;
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const event = parseSseEvent(chunk);
        if (event) onEvent(event);
      }
    }
  })();

  return {
    close() {
      if (closed) return;
      closed = true;
      controller.abort();
    },
    ready,
  };
}

function parseSseEvent(chunk: string): SharedConfigStreamEvent | null {
  const lines = chunk.split(/\r?\n/);
  let eventName = "";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) { eventName = line.slice("event:".length).trim(); continue; }
    if (line.startsWith("data:")) { dataLines.push(line.slice("data:".length).trim()); }
  }

  if (!eventName || dataLines.length === 0) return null;
  const data = JSON.parse(dataLines.join("\n")) as SharedConfigStreamEvent;
  return { ...data, event: eventName as SharedConfigStreamEvent["event"] };
}
